import type { ApiResponseEnvelope } from '@/shared/api/types/api-response'

/**
 * Unwraps the standard API envelope, failing loudly when the body is not one.
 *
 * The runtime guards are deliberately wider than the declared type: when a request
 * lands somewhere that does not speak the envelope (misconfigured base URL, SPA
 * fallback HTML, gateway error body), `data` is *absent* rather than `null`.
 * Returning `undefined` in that case pushes the failure downstream, where it
 * surfaces as an opaque `Cannot read properties of undefined` at the property
 * access instead of naming the real problem here.
 *
 * @param envelope - Parsed response body, as received from the API.
 * @returns The envelope payload, guaranteed non-nullish.
 * @throws If the body is not an envelope object, or carries no payload.
 */
export function unwrapApiEnvelope<T>(envelope: ApiResponseEnvelope<T> | null | undefined): T {
  if (envelope === null || envelope === undefined || typeof envelope !== 'object') {
    throw new Error('API returned an unexpected response shape')
  }

  if (envelope.data === null || envelope.data === undefined) {
    throw new Error(envelope.message ?? 'API returned empty data')
  }

  return envelope.data
}
